import type { Dispatch, AnyAction } from 'redux';

import type { BackendBillingCodeErrors } from 'af-root/scenes/Company/FieldReports/FieldReportsList/FieldReport/WorkSummary/BillableAndNonBillableWork/Table/EditBillingCodesModal';

import * as EditBillingCodesAPI from 'ab-api/web/billingCode/editBillingCodes';
import type { W_BillingCode_UpdateBillingCodes_RM } from 'ab-api/web/billingCode/editBillingCodes';
import * as FindAllAvailableSubjobsAPI from 'ab-api/web/job/findAllAvailableSubjobs';
import * as FindAllForPurchaseOrderOptionsAPI from 'ab-api/web/job/findAllForPurchaseOrderOptions';
import * as FindAllSubjobsAPI from 'ab-api/web/job/findAllSubjobs';
import * as FindAllSubjobsTableAPI from 'ab-api/web/job/findAllSubjobsTable';
import * as FindJobReportsListAPI from 'ab-api/web/job/findJobReportsList';
import * as FindOrCreateJobReportsLookupBulkGroupCodeAPI from 'ab-api/web/job/findOrCreateJobReportsLookupBulkGroupCode';
import * as FindSiblingSubjobsAPI from 'ab-api/web/job/findSiblingSubjobs';
import * as CreateWorkRequestAPI from 'ab-api/web/workRequest/create';
import * as EditWorkRequestAPI from 'ab-api/web/workRequest/edit';
import * as UpdateBidStatusAPI from 'ab-api/web/workRequest/editWorkRequestBidStatus';
import * as WorkRequestFieldReportFilterOptionsAPI from 'ab-api/web/workRequest/findAllForFieldReportFilter';
import * as WorkRequestFindAllForOrderReportFilterAPI from 'ab-api/web/workRequest/findAllForOrderReportFilter';
import * as FindForEditAPI from 'ab-api/web/workRequest/findWorkRequestForEdit';
import * as WorkRequestsTableAPI from 'ab-api/web/workRequest/findWorkRequestsTable';

import type { BillingCodeVM } from 'ab-viewModels/jobPreview.viewModel';
import type ConvertedToJobVM from 'ab-viewModels/workRequest/convertedToJob.viewModel';
import type CopiedWorkRequestVM from 'ab-viewModels/workRequest/copiedWorkRequest.viewModel';
import type JobWithProjectInfoVM from 'ab-viewModels/workRequest/jobWithProjectInfo.viewModel';
import type WorkRequestPreviewVM from 'ab-viewModels/workRequest/workRequestPreview.viewModel';

import type WorkRequestConvertToJobRM from 'ab-requestModels/workRequest/workRequestConvertToJob.requestModel';
import type WorkRequestCopyRM from 'ab-requestModels/workRequest/workRequestCopy.requestModel';

import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';
import type { WorkRequestBidStatus } from '@acceligentllc/shared/enums/workRequestBidStatus';

import type { GetRootState } from 'af-reducers';

import type { ErrorOverride, ResponseDetails } from 'af-utils/actions.util';
import { errorHandler } from 'af-utils/actions.util';
import { http } from 'af-utils/http.util';

import { CONVERT_TO_JOB, WORK_REQUEST_COPY, WORK_REQUEST_FORM } from 'af-constants/reduxForms';

import type { TableContent } from 'ab-common/dataStructures/tableContent';
import { TableQuery } from 'ab-common/dataStructures/tableQuery';

import API from 'af-routes/api';

export function getWorkRequestsTable(tableRequestModel: TableQuery, bidStatus: WorkRequestBidStatus) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const data = new TableQuery(tableRequestModel);
			return await http.get<WorkRequestsTableAPI.W_WorkRequest_FindWorkRequestsTable_VM>(WorkRequestsTableAPI.URL(data, bidStatus));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function create(workRequestRM: CreateWorkRequestAPI.W_WorkRequest_Create_RM) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.post<CreateWorkRequestAPI.W_WorkRequest_Create_VM>(CreateWorkRequestAPI.URL(), workRequestRM);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function edit(id: number, workRequestRM: Partial<EditWorkRequestAPI.W_WorkRequest_Edit_RM>) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.put<void>(EditWorkRequestAPI.URL(id), workRequestRM, { submitting: WORK_REQUEST_FORM });
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function editBidStatus(id: number, workRequestBidStatusRM: UpdateBidStatusAPI.W_WorkRequest_EditWorkRequestBidStatus_RM) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.put<void>(UpdateBidStatusAPI.URL(id), workRequestBidStatusRM);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function convertToJob(id: number, workRequestConvertToJobRM: WorkRequestConvertToJobRM) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.post<ConvertedToJobVM>(API.V1.WORK_REQUEST.CONVERT_TO_JOB(id), workRequestConvertToJobRM, { submitting: CONVERT_TO_JOB });
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function copy(id: number, workRequestCopyRM: WorkRequestCopyRM) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.post<CopiedWorkRequestVM>(API.V1.WORK_REQUEST.COPY(id), workRequestCopyRM, { submitting: WORK_REQUEST_COPY });
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findByIdForEdit(id: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<FindForEditAPI.W_WorkRequest_FindWorkRequestForEdit_VM>(FindForEditAPI.URL(id));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findByIdForPreview(id: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<WorkRequestPreviewVM>(API.V1.WORK_REQUEST.FIND_BY_ID_FOR_PREVIEW(id));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllAvailableSubjobs(jobId?: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const response = await http.get<FindAllAvailableSubjobsAPI.W_Job_FindAllAvailableSubjobs_VM>(FindAllAvailableSubjobsAPI.URL(jobId));
			return response.subjobs;
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllSubjobs(id: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const result = await http.get<FindAllSubjobsAPI.W_Job_FindAllSubjobs_VM>(FindAllSubjobsAPI.URL(id));
			return result.subjobs;
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findJobReportsList(startDate: Date, endDate: Date) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			const startDateString = TimeUtils.formatDate(startDate, TimeFormat.DB_DATE_ONLY);
			const endDateString = TimeUtils.formatDate(endDate, TimeFormat.DB_DATE_ONLY);

			const result = await http.get<FindJobReportsListAPI.W_Job_FindJobReportsList_VM>(
				FindJobReportsListAPI.URL(startDateString, endDateString)
			);
			return result.jobs;
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllSubjobsTable(id: number, tableRequestModel: TableQuery) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const _tableRequestModel = {
			...tableRequestModel,
			page: 1,
			pageSize: 100000,
		};

		const action = async () => {
			return await http.get<FindAllSubjobsTableAPI.W_Job_FindAllSubjobsTable_VM>(FindAllSubjobsTableAPI.URL(id, _tableRequestModel));
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findOrCreateJobReportsBulkGroupCode(ids: number[], /** YYYY-MM-DD */ startDate: string, /** YYYY-MM-DD */ endDate: string) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			const data: FindOrCreateJobReportsLookupBulkGroupCodeAPI.W_Job_FindOrCreateJobReportsLookupBulkGroupCode_RM = { ids, startDate, endDate };
			const result = await http.post<FindOrCreateJobReportsLookupBulkGroupCodeAPI.W_Job_FindOrCreateJobReportsLookupBulkGroupCode_VM>(
				FindOrCreateJobReportsLookupBulkGroupCodeAPI.URL(),
				data
			);
			return result.code;
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllBillingCodesTable(id: number, tableRequestModel: TableQuery) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const _tableRequestModel = {
			...tableRequestModel,
			page: 1,
			pageSize: 100000,
		};

		const action = async () => {
			return await http.get<TableContent<BillingCodeVM>>(API.V1.WORK_REQUEST.FIND_ALL_BILLING_CODES_TABLE(id, _tableRequestModel));
		};
		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findSiblingSubjobs(jobId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			const response = await http.get<FindSiblingSubjobsAPI.W_Job_FindSiblingSubjobs_VM>(FindSiblingSubjobsAPI.URL(jobId));
			return response.subjobs;
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findWithProjectInfo(jobId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			return await http.get<JobWithProjectInfoVM>(API.V1.WORK_REQUEST.FIND_WITH_PROJECT_INFO(jobId));
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findWorkRequestOptionsForPurchaseOrder() {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {
		const action = async () => {
			const response = await http.get<FindAllForPurchaseOrderOptionsAPI.W_Job_FindAllForPurchaseOrderOption_VM>(FindAllForPurchaseOrderOptionsAPI.URL());
			return response.jobs;
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllForFieldReportFilter() {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<WorkRequestFieldReportFilterOptionsAPI.W_WorkRequest_FindAllForFieldReportFilter_VM>(
				WorkRequestFieldReportFilterOptionsAPI.URL()
			);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function findAllForOrderReportFilter() {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }) => {

		const action = async () => {
			return await http.get<WorkRequestFindAllForOrderReportFilterAPI.W_WorkRequest_FindAllForOrderReportFilter_VM>(
				WorkRequestFindAllForOrderReportFilterAPI.URL()
			);
		};

		return await errorHandler(action, dispatch, redirectTo);
	};
}

export function updateBillingCodes(billingCodes: W_BillingCode_UpdateBillingCodes_RM, jobId: number) {
	return async (dispatch: Dispatch<AnyAction>, getState: GetRootState, { redirectTo }): Promise<ResponseDetails<BackendBillingCodeErrors>> => {

		const action = async () => {
			await http.post<void>(EditBillingCodesAPI.URL(jobId), billingCodes);
			return { success: true };
		};
		const error: ErrorOverride = {
			err400: ({ response }) => {
				return { success: false, details: response?.data };
			},
		};

		return await errorHandler(action, dispatch, redirectTo, error);
	};
}

